import { InfoDivider, Loading, Typography } from "@lyfegen-platform/react-common";
import { LiteBox } from "../../../../components/lite/LiteBox";
import { useTranslation } from "react-i18next";
import useOrganizationStyles from "../use-organization-styles";
import { CREATE_DOMAIN } from "../queries";
import { useHistory, useParams } from "react-router";
// import { useMutation } from "@apollo/client";
import { Box } from "@material-ui/core";
import { useDomainBreadcrumbs } from "./useDomainBreadcrumbs";
import { DomainForm } from "./components/DomainForm";
import { useOrganizationDomains } from "./useOrganizationDomains";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { userPersonalInfoState } from "apps/platform-client/client/src/store/user";
import { globalDomainId } from "@lyfegen-platform/graphql-schema/models";
import { Role } from "apps/platform-client/client/src/models/identity/role";
import SpecificErrorPage from "apps/platform-client/client/src/components/SpecificErrorPage";
import { useModelsMutation } from "apps/platform-client/client/src/hooks/useModelsModule";
import { Link } from "react-router-dom";
import { findDomainById, isGlobalOrProductUser } from "./helpers";
import { DomainType } from "./components/DomainCard";

export const CreateDomain = () => {
	const { data, loading } = useOrganizationDomains();
	const [mutation, { error }] = useModelsMutation(CREATE_DOMAIN);
	const { tableColumnTextColor, organizationActionSettingsContainer, pageName } = useOrganizationStyles();
	const { t } = useTranslation();
	const { goBack } = useHistory();
	const { domain } = useParams<{ domain: string }>();
	const [isOpenErrorBanner, setIsOpenErrorBanner] = useState(false);
	const domainData = data?.GetOrganizationDomainsWithCounters;
	const usersDomain = domainData?.domains[0];
	const isProductDomainCreation = domain === usersDomain?.id && usersDomain.level == 0;
	const { currentDomain } = findDomainById(domainData?.domains as DomainType[], domain);
	const unavailableDomainNames: string[] = domainData?.names || [];
	const localizationKeys = isProductDomainCreation
		? {
				breadcrumbs: "create-new-domain",
				title: "create-new-domain-header",
				firstSection: "simulator-product-domain",
				label: "simulator-product-domain-name",
		  }
		: {
				breadcrumbs: "create-new-subdomain",
				title: "create-new-subdomain-header",
				firstSection: "simulator-product-subdomain",
				label: "simulator-product-subdomain-name",
		  };

	const user = useRecoilValue(userPersonalInfoState);
	const isAdmin = user.role === Role.Administrator;
	const canAccessDomains = isGlobalOrProductUser(user.domains);

	useDomainBreadcrumbs({
		global: {
			id: globalDomainId,
			name: domainData?.domains.find((domain) => domain.id === globalDomainId)?.name || "",
		},
		product: {
			id: "",
			name: "",
		},
		childDomainName: t(localizationKeys.breadcrumbs),
	});

	useEffect(() => {
		error ? setIsOpenErrorBanner(true) : setIsOpenErrorBanner(false);
	}, [error]);

	if (loading)
		return (
			<LiteBox width="100%" height="100%" justifyContent="center" alignItems="center" display="flex" padding="20vh">
				<Loading />
			</LiteBox>
		);

	if (!isAdmin || !canAccessDomains || !currentDomain) {
		return <SpecificErrorPage statusCode="403" />;
	}

	const domainOptions = [{ name: currentDomain.name, id: currentDomain.id }];
	const initialValues = { parentDomainId: currentDomain.id, name: "" };

	const handleSubmit = (values: { parentDomainId: string; name: string }) => {
		mutation({
			variables: { input: values },
			onCompleted: () => {
				goBack();
			},
			onError: (error) => {
				console.error("Error creating domain:", error);
			},
		});
	};

	return (
		<LiteBox>
			<LiteBox paddingBottom={3}>
				<Typography className={pageName}>{t(localizationKeys.title)}</Typography>
			</LiteBox>
			<Box className={organizationActionSettingsContainer}>
				<DomainForm
					initialValues={initialValues}
					domainOptions={domainOptions}
					unavailableDomainNames={unavailableDomainNames}
					handleSubmit={handleSubmit}
					isError={isOpenErrorBanner}
					error={error}
					label={localizationKeys.label}
					title={localizationKeys.firstSection}
					dismissBanner={() => setIsOpenErrorBanner(false)}
				/>
				<InfoDivider paddingX={2} paddingTop={5} title="domain-header-users" />
				<LiteBox minHeight={250} paddingTop={2} paddingLeft={2}>
					<Typography className={tableColumnTextColor}>
						{t("domain-creation-users-empty")}{" "}
						<Link color="primary" to={"/organization-settings"}>
							{t("organization-settings-tab-users")}
						</Link>{" "}
						{t("tab")}
					</Typography>
				</LiteBox>
			</Box>
		</LiteBox>
	);
};
